import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { submitQuoteRequest } from "@/lib/quoteRequest";
import { toast } from "sonner";
import { useQuoteModal, type LeadFormType } from "@/hooks/useQuoteModal";
import { Zap, Lock, Phone, ArrowRight, ArrowLeft, Truck, Package, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";
import { motion, AnimatePresence } from "framer-motion";
import { CityCombobox } from "@/components/CityCombobox";
import { CityOption, cityLabel, distanceMiles } from "@/data/cities";
import { FREIGHT_TYPE_OPTIONS, EQUIPMENT_BY_FREIGHT_TYPE } from "@/data/services";
import { cn } from "@/lib/utils";

const emptyForm = {
  originCity: "", originState: "", destCity: "", destState: "",
  freightType: "", weight: "", commodity: "",
  name: "", company: "", email: "", phone: "",
  website: "",
};

type FormData = typeof emptyForm;
type FormErrors = Partial<Record<keyof FormData, string>>;

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const requiredMessage = "This field is required.";

export const formatPhone = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const formatWeight = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 7);
  return digits ? Number(digits).toLocaleString("en-US") : "";
};

const normalize = (data: FormData) => ({
  ...data,
  originCity: data.originCity.trim(),
  originState: data.originState.trim().toUpperCase(),
  destCity: data.destCity.trim(),
  destState: data.destState.trim().toUpperCase(),
  commodity: data.commodity.trim(),
  name: data.name.trim(),
  company: data.company.trim(),
  email: data.email.trim().toLowerCase(),
  phone: formatPhone(data.phone),
});

const fieldLabels: Partial<Record<keyof FormData, string>> = {
  originCity: "Origin city",
  originState: "Origin state/province",
  destCity: "Destination city",
  destState: "Destination state/province",
  freightType: "Freight type",
  weight: "Weight",
  commodity: "Commodity",
  name: "Name",
  company: "Company",
  email: "Email",
  phone: "Phone number",
};

export const validateLeadForm = (data: FormData, formType: LeadFormType, step?: number) => {
  const nextErrors: FormErrors = {};
  const normalized = normalize(data);
  const requiredByStep: Record<number, (keyof FormData)[]> = {
    1: ["originCity", "originState", "destCity", "destState"],
    2: ["freightType", "weight", "commodity"],
    3: ["name", "company", "email", "phone"],
  };
  const fields = formType === "contact"
    ? (["name", "company", "email", "phone"] as (keyof FormData)[])
    : step
      ? (requiredByStep[step] ?? [])
      : ([...(requiredByStep[1] ?? []), ...(requiredByStep[2] ?? []), ...(requiredByStep[3] ?? [])] as (keyof FormData)[]);

  fields.forEach((field) => {
    if (!normalized[field]) nextErrors[field] = requiredMessage;
  });

  if (fields.includes("email") && normalized.email && !emailPattern.test(normalized.email)) {
    nextErrors.email = "Enter a valid email address.";
  }

  if (fields.includes("phone") && normalized.phone.replace(/\D/g, "").length < 10) {
    nextErrors.phone = "Enter a 10-digit phone number.";
  }

  if (fields.includes("weight") && normalized.weight.replace(/\D/g, "").length === 0) {
    nextErrors.weight = "Enter the shipment weight.";
  }

  return nextErrors;
};

const QuoteModal = () => {
  const { isOpen, close, context } = useQuoteModal();
  const formType = context.formType;
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startedAt, setStartedAt] = useState(Date.now());
  const [errors, setErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [origin, setOrigin] = useState<CityOption | null>(null);
  const [destination, setDestination] = useState<CityOption | null>(null);
  const [originZip, setOriginZip] = useState("");
  const [destZip, setDestZip] = useState("");
  const [equipment, setEquipment] = useState("");
  const [pickupReadiness, setPickupReadiness] = useState("");
  const [commodityCategory, setCommodityCategory] = useState("");

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setErrors({});
      setStartedAt(Date.now());
      setOrigin(null);
      setDestination(null);
      setOriginZip("");
      setDestZip("");
      setEquipment("");
      setPickupReadiness("");
      setCommodityCategory("");
      setFormData(emptyForm);
    }
  }, [isOpen, formType]);

  const update = (field: keyof FormData, value: string) => {
    const formatted = field === "phone"
      ? formatPhone(value)
      : field === "weight"
        ? formatWeight(value)
        : field === "originState" || field === "destState"
          ? value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3)
          : value;

    setFormData((prev) => ({ ...prev, [field]: formatted }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const reset = () => {
    setStep(1);
    setErrors({});
    setFormData(emptyForm);
    setOrigin(null);
    setDestination(null);
    setOriginZip("");
    setDestZip("");
    setEquipment("");
    setPickupReadiness("");
    setCommodityCategory("");
  };

  const validateStep = () => {
    if (formType === "contact") {
      const next = validateLeadForm(formData, "contact");
      setErrors(next);
      return Object.keys(next).length === 0;
    }
    const next: FormErrors = {};
    if (step === 1) {
      if (!origin) next.originCity = "Select an origin from the suggestions.";
      if (!destination) next.destCity = "Select a destination from the suggestions.";
      if (origin && destination && origin.city === destination.city && origin.state === destination.state) {
        next.destCity = "Destination must differ from origin.";
      }
    } else if (step === 2) {
      if (!formData.freightType) next.freightType = requiredMessage;
      const w = Number(formData.weight.replace(/,/g, ""));
      if (!formData.weight) next.weight = requiredMessage;
      else if (w < 1) next.weight = "Enter a weight greater than 0.";
      else if (w > 80000) next.weight = "Max 80,000 lbs for standard FTL. Call us for heavy haul.";
      if (!commodityCategory) next.commodity = "Select a commodity.";
      else if (commodityCategory === "Other" && !formData.commodity.trim()) next.commodity = "Describe your commodity.";
    } else if (step === 3) {
      const contactErrors = validateLeadForm(formData, "quote", 3);
      Object.assign(next, contactErrors);
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      trackEvent("quote_step_completed", { step, form_type: formType, cta_source: context.ctaSource });
      setStep((current) => current + 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setIsSubmitting(true);
    const normalized = normalize(formData);
    const commodityValue = commodityCategory === "Other" ? formData.commodity.trim() : commodityCategory;
    const lanePayload = formType === "quote" && origin && destination ? {
      originCity: origin.city,
      originState: origin.state,
      destCity: destination.city,
      destState: destination.state,
      originZip: originZip || undefined,
      destZip: destZip || undefined,
    } : {};

    const { error } = await submitQuoteRequest({
        ...normalized,
        ...lanePayload,
        commodity: formType === "quote" ? commodityValue : normalized.commodity,
        equipment: formType === "quote" ? equipment || undefined : undefined,
        pickupReadiness: formType === "quote" ? pickupReadiness || undefined : undefined,
        requestType: formType,
        ctaSource: context.ctaSource,
        pagePath: context.pagePath || window.location.pathname,
        startedAt,
    });

    setIsSubmitting(false);

    if (error) {
      trackEvent("lead_submit_error", { form_type: formType, cta_source: context.ctaSource });
      toast.error("We couldn't submit your request. Please call (855) 226-7123.");
      return;
    }

    trackEvent("lead_submit_success", { form_type: formType, cta_source: context.ctaSource });
    toast.success(formType === "quote" ? "Thanks! We'll quote this as quickly as possible." : "Thanks! We'll be in touch shortly.");
    reset();
    close();
  };

  const fieldError = (field: keyof FormData) => errors[field] ? (
    <p className="mt-1 text-xs font-medium text-destructive">{errors[field]}</p>
  ) : null;

  const requiredLabel = (field: keyof FormData) => `${fieldLabels[field]} *`;
  const modalTitle = formType === "quote" ? "Get a Freight Quote" : "Get in Touch";
  const stepLabels = ["Lane", "Freight", "Contact"] as const;
  const stepIcons = [Truck, Package, User];
  const distance = origin && destination ? distanceMiles(origin, destination) : null;
  const FREIGHT_TYPES = [...FREIGHT_TYPE_OPTIONS, "Other"];
  const EQUIPMENT_BY_TYPE: Record<string, string[]> = {
    ...EQUIPMENT_BY_FREIGHT_TYPE,
    Other: ["Tell us in notes"],
  };
  const COMMODITIES = ["Auto parts", "Food & beverage", "Building materials", "Machinery", "Retail / consumer goods", "Hazmat", "Other"];

  const contactFields = (
    <div className="space-y-4" role="group" aria-label="Contact information">
      <div className="hidden" aria-hidden="true"><Label htmlFor="q-website">Website</Label><Input id="q-website" tabIndex={-1} autoComplete="off" value={formData.website} onChange={(e) => update("website", e.target.value)} /></div>
      <div><Label htmlFor="q-name">{requiredLabel("name")}</Label><Input id="q-name" autoComplete="name" value={formData.name} onChange={(e) => update("name", e.target.value)} placeholder="John Smith" aria-invalid={!!errors.name} />{fieldError("name")}</div>
      <div><Label htmlFor="q-company">{requiredLabel("company")}</Label><Input id="q-company" autoComplete="organization" value={formData.company} onChange={(e) => update("company", e.target.value)} placeholder="Acme Corp" aria-invalid={!!errors.company} />{fieldError("company")}</div>
      <div><Label htmlFor="q-email">{requiredLabel("email")}</Label><Input id="q-email" type="email" autoComplete="email" value={formData.email} onChange={(e) => update("email", e.target.value)} placeholder="john@acme.com" aria-invalid={!!errors.email} />{fieldError("email")}</div>
      <div><Label htmlFor="q-phone">{requiredLabel("phone")}</Label><Input id="q-phone" type="tel" inputMode="tel" autoComplete="tel" value={formData.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(555) 123-4567" aria-invalid={!!errors.phone} />{fieldError("phone")}</div>
      {formType === "quote" && origin && destination && (
        <p className="text-xs text-muted-foreground border-t border-border pt-3">
          Quoting <span className="font-semibold text-foreground">{cityLabel(origin)} → {cityLabel(destination)}</span>
          {distance ? <> · ~{distance.toLocaleString()} mi</> : null}
        </p>
      )}
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        By submitting, you agree to be contacted about this shipment. We never sell your information.
      </p>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
      <DialogContent className="sm:max-w-lg" aria-modal="true" aria-describedby="quote-step-description">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-foreground">{modalTitle}</DialogTitle>
        </DialogHeader>

        {formType === "quote" && (
          <div className="mb-2" aria-label={`Quote form progress: step ${step} of 3`}>
            <div className="flex items-center gap-2">
              {stepLabels.map((label, idx) => {
                const stepNum = idx + 1;
                const Icon = stepIcons[idx]!;
                const isActive = stepNum === step;
                const isDone = stepNum < step;
                const clickable = stepNum < step;
                return (
                  <div key={label} className="flex items-center gap-2 flex-1">
                    <button
                      type="button"
                      disabled={!clickable}
                      onClick={() => clickable && setStep(stepNum)}
                      className={cn(
                        "flex items-center gap-2 transition-colors",
                        clickable && "cursor-pointer hover:opacity-80",
                      )}
                      aria-current={isActive ? "step" : undefined}
                    >
                      <span className={cn(
                        "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors",
                        isActive && "bg-electric text-white shadow-[0_0_0_4px_hsl(var(--electric)/0.15)]",
                        isDone && "bg-electric/15 text-electric",
                        !isActive && !isDone && "bg-muted text-muted-foreground",
                      )}>
                        <Icon className="w-3.5 h-3.5" />
                      </span>
                      <span className={cn(
                        "text-xs font-semibold uppercase tracking-wider",
                        isActive ? "text-foreground" : "text-muted-foreground",
                      )}>{label}</span>
                    </button>
                    {idx < stepLabels.length - 1 && (
                      <div className={cn("h-px flex-1 transition-colors", stepNum < step ? "bg-electric/40" : "bg-border")} />
                    )}
                  </div>
                );
              })}
            </div>
            <p id="quote-step-description" className="sr-only">Step {step} of 3: {stepLabels[step - 1]}</p>
          </div>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={`${formType}-${step}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
          >
            {formType === "quote" && step === 1 && (
              <div className="space-y-4" role="group" aria-label="Origin and destination">
                <CityCombobox
                  label="Pickup city"
                  placeholder="Start typing — e.g. Detroit"
                  value={origin}
                  onChange={(v) => { setOrigin(v); setErrors(({ originCity: _oc, ...p }) => p); if (!v) setOriginZip(""); }}
                  error={errors.originCity}
                  autoComplete="address-level2"
                  zip={originZip}
                  onZipChange={setOriginZip}
                />
                <CityCombobox
                  label="Delivery city"
                  placeholder="Start typing — e.g. Toronto"
                  value={destination}
                  onChange={(v) => { setDestination(v); setErrors(({ destCity: _dc, ...p }) => p); if (!v) setDestZip(""); }}
                  error={errors.destCity}
                  zip={destZip}
                  onZipChange={setDestZip}
                />
                {origin && destination && (
                  <div className="flex items-center justify-center gap-2 rounded-lg bg-electric/5 border border-electric/20 px-3 py-2.5 text-sm">
                    <span className="font-semibold text-foreground">{cityLabel(origin)}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-electric" />
                    <span className="font-semibold text-foreground">{cityLabel(destination)}</span>
                    {distance && <span className="text-muted-foreground">· ~{distance.toLocaleString()} mi</span>}
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground">Pick a city from the dropdown — that way we can match you to a carrier in seconds.</p>
              </div>
            )}

            {formType === "quote" && step === 2 && (
              <div className="space-y-4" role="group" aria-label="Freight details">
                <div>
                  <Label htmlFor="q-freight-type">{requiredLabel("freightType")}</Label>
                  <Select value={formData.freightType} onValueChange={(v) => { update("freightType", v); setEquipment(""); }}>
                    <SelectTrigger id="q-freight-type" aria-invalid={!!errors.freightType}><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>{FREIGHT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                  {fieldError("freightType")}
                </div>

                {formData.freightType && (
                  <div>
                    <Label htmlFor="q-equipment">Equipment / size</Label>
                    <Select value={equipment} onValueChange={setEquipment}>
                      <SelectTrigger id="q-equipment"><SelectValue placeholder="Optional — helps us quote faster" /></SelectTrigger>
                      <SelectContent>{(EQUIPMENT_BY_TYPE[formData.freightType] || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label htmlFor="q-weight">{requiredLabel("weight")} (lbs)</Label>
                  <Input id="q-weight" inputMode="numeric" value={formData.weight} onChange={(e) => update("weight", e.target.value)} placeholder="10,000" aria-invalid={!!errors.weight} />
                  <p className="mt-1 text-[11px] text-muted-foreground">Standard FTL max 80,000 lbs. Heavier? Call (855) 226-7123 for heavy haul.</p>
                  {fieldError("weight")}
                </div>

                <div>
                  <Label htmlFor="q-commodity">{requiredLabel("commodity")}</Label>
                  <Select value={commodityCategory} onValueChange={(v) => { setCommodityCategory(v); setErrors(({ commodity: _cm, ...p }) => p); if (v !== "Other") setFormData((p) => ({ ...p, commodity: "" })); }}>
                    <SelectTrigger id="q-commodity" aria-invalid={!!errors.commodity}><SelectValue placeholder="Select commodity" /></SelectTrigger>
                    <SelectContent>{COMMODITIES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                  {commodityCategory === "Other" && (
                    <Input className="mt-2" value={formData.commodity} onChange={(e) => update("commodity", e.target.value)} placeholder="Describe your commodity" aria-invalid={!!errors.commodity} />
                  )}
                  {fieldError("commodity")}
                </div>

                <div>
                  <Label>Pickup readiness</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {["Today", "This week", "Flexible"].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setPickupReadiness(opt)}
                        className={cn(
                          "h-10 rounded-md border text-sm font-medium transition-colors",
                          pickupReadiness === opt
                            ? "border-electric bg-electric/10 text-foreground"
                            : "border-input bg-background text-muted-foreground hover:text-foreground hover:border-electric/40",
                        )}
                      >{opt}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {(formType === "contact" || step === 3) && contactFields}
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-between items-center mt-6 gap-3">
          {formType === "quote" && step > 1 ? (
            <Button variant="outline" onClick={() => setStep(step - 1)} className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          ) : <div />}
          {formType === "quote" && step < 3 ? (
            <Button className="bg-electric text-white hover:bg-electric/90 rounded-full font-semibold px-6 ml-auto" onClick={handleNext}>
              Continue <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button className="bg-electric text-white hover:bg-electric/90 rounded-full font-semibold px-6 ml-auto" disabled={isSubmitting} onClick={handleSubmit}>
              {isSubmitting ? "Submitting..." : formType === "quote" ? "Request My Quote" : "Submit"}
            </Button>
          )}
        </div>

        <div className="flex items-center justify-center gap-4 mt-4 pt-3 border-t border-border text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1"><Zap className="w-3 h-3 text-electric" /> 2-hr response</span>
          <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Info stays private</span>
          <a href="tel:+18552267123" className="inline-flex items-center gap-1 hover:text-foreground transition-colors"><Phone className="w-3 h-3" /> (855) 226-7123</a>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteModal;
